import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const cspUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Algorithms and Programming",
  examWeight: "30–35%",
  focus: "Programmers integrate algorithms and abstraction to create programs for creative purposes and to solve problems.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Variables and Assignments",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Core Concepts</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Variable" 
                definition="An abstraction inside a program that can hold a value. It has a name and associated data storage."
              />
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2">Data Types</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                  <li><strong>Numbers:</strong> Integers (whole numbers) or floats (decimals).</li>
                  <li><strong>Booleans:</strong> Only two possible values: <code className="text-pink-600">true</code> or <code className="text-pink-600">false</code>.</li>
                  <li><strong>Strings:</strong> An ordered sequence of characters (text).</li>
                  <li><strong>Lists:</strong> An ordered sequence of elements.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 The Assignment Operator</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The assignment operator allows a program to change the value represented by a variable.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">AP Syntax: <code className="bg-white px-2 py-1 rounded">a ← expression</code></h4>
              <p className="text-sm text-slate-700 mt-2">
                <strong>Read as:</strong> "Get the value of <em>expression</em> and assign it to variable <em>a</em>."
              </p>
              <p className="text-sm text-slate-600 mt-2 italic">
                Note: The computer evaluates the right side FIRST, then stores it in the left.
              </p>
            </div>
            <ImportantNote>
              <strong>Updating Variables:</strong> Variables are often updated based on their current value. For <code className="font-mono">score ← score + 1</code>, the computer retrieves the current score, adds 1, and overwrites the old value.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Data Abstraction",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <KeyTerm 
                term="Abstraction" 
                definition="The process of reducing complexity by focusing on the main idea. It hides irrelevant details."
              />
              <KeyTerm 
                term="Data Abstraction" 
                definition="Managing complexity by giving a collection of data a name (like a List) without referencing the specific details of the representation."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💡 Why use Lists?</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Readability</h4>
                <p className="text-xs text-slate-700 mt-1">Easier to handle <code className="bg-white px-1">studentList</code> than 100 individual variables.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Scalability</h4>
                <p className="text-xs text-slate-700 mt-1">A program can handle 10 items or 10,000 items without rewriting code.</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">Management</h4>
                <p className="text-xs text-slate-700 mt-1">Separates abstract properties from the concrete details of storage.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Mathematical Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧮 Arithmetic Operators</h3>
            <div className="grid grid-cols-5 gap-2 mb-4 text-center">
              {['+', '-', '*', '/', 'MOD'].map(op => (
                <div key={op} className="bg-slate-100 p-2 rounded border border-slate-300 font-mono font-bold text-slate-700">{op}</div>
              ))}
            </div>
            <p className="text-sm text-slate-600 italic mb-4">Operators follow standard PEMDAS order of operations.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔢 Understanding MOD (Modulus)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">MOD returns the <strong>remainder</strong> of integer division.</p>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 space-y-3">
              <div className="flex items-center gap-4">
                <code className="bg-white px-2 py-1 rounded">17 MOD 5</code>
                <span className="text-slate-500">→</span>
                <span className="font-bold text-indigo-900">2</span>
                <span className="text-xs text-slate-500">(5 goes into 17 three times, remainder is 2)</span>
              </div>
              <div className="flex items-center gap-4">
                <code className="bg-white px-2 py-1 rounded">4 MOD 6</code>
                <span className="text-slate-500">→</span>
                <span className="font-bold text-indigo-900">4</span>
                <span className="text-xs text-slate-500">(6 goes into 4 zero times, remainder is 4)</span>
              </div>
            </div>
            <div className="mt-4">
              <h4 className="font-bold text-slate-800 text-sm mb-2">Common Use Cases:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Determining even/odd: <code className="bg-slate-100 px-1">x MOD 2</code>. If 0, it's even.</li>
                <li>Time conversions (minutes to hours).</li>
                <li>Cycling through items in a list.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Strings",
      content: (
        <div className="space-y-6">
          <section>
            <KeyTerm 
              term="String" 
              definition="An ordered sequence of characters (letters, numbers, symbols)."
            />
            <div className="bg-pink-50 p-4 rounded-lg border border-pink-200 mt-4">
              <h4 className="font-bold text-pink-900">Concatenation</h4>
              <p className="text-sm text-slate-700 mt-1">Joining two or more strings end-to-end to make a new string.</p>
              <div className="mt-3 bg-white p-2 rounded border border-pink-100 text-sm font-mono">
                "Hello" + " " + "World" → "Hello World"
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">Note: Spaces must be explicitly added as their own string character.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Boolean Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Relational Operators</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {['=', '≠', '>', '<', '≥', '≤'].map(op => (
                <div key={op} className="bg-white p-3 text-center rounded border border-slate-200 shadow-sm font-mono font-bold text-purple-700 text-lg">
                  {op}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 Logical Operators</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">NOT</h4>
                <p className="text-sm text-slate-700 mt-1">Reverses the Boolean value. <code className="bg-white px-1">NOT true</code> is <code className="bg-white px-1">false</code>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">AND</h4>
                <p className="text-sm text-slate-700 mt-1">True only if <strong>BOTH</strong> operands are true.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">OR</h4>
                <p className="text-sm text-slate-700 mt-1">True if <strong>AT LEAST ONE</strong> operand is true.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Conditionals (Selection)",
      content: (
        <div className="space-y-6">
          <section>
            <KeyTerm 
              term="Selection" 
              definition="Determines which parts of an algorithm execute based on a condition being true or false."
            />
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-300 mt-4">
              <h4 className="font-bold text-slate-800">Flowcharts</h4>
              <p className="text-sm text-slate-700">Represented by a <strong>diamond</strong> shape.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📝 Syntax Types</h3>
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">1. IF Statement</h4>
                <p className="text-sm text-slate-600 italic">Code runs only if the condition is true.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. IF-ELSE Statement</h4>
                <p className="text-sm text-slate-600 mb-2 italic">One of the two blocks MUST execute.</p>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• If true: First block runs.</li>
                  <li>• If false: Second (ELSE) block runs.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Nested Conditionals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">层 Nesting Logic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Placing a conditional statement <em>inside</em> another.
            </p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <h4 className="font-bold text-red-900 mb-2">Logic Tracing</h4>
              <p className="text-sm text-slate-700">You must satisfy the <strong>outer condition</strong> first before the computer even evaluates the inner condition.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Equivalent Logic</h4>
              <p className="text-sm text-slate-700">Nested IFs can often be rewritten as one IF with an <code className="font-bold text-green-700">AND</code> operator.</p>
              <div className="mt-2 text-xs font-mono bg-white p-2 rounded">
                IF (isSunny) {'{'} IF (isWarm) {'{'} goSwim() {'}'} {'}'} <br/>
                == IF (isSunny AND isWarm) {'{'} goSwim() {'}'}
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: Iteration (Loops)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔁 Types of Loops</h3>
            <div className="space-y-4">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">1. REPEAT n TIMES</h4>
                <p className="text-sm text-slate-700 mt-1">Executes the block exactly <em>n</em> times.</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-900">2. REPEAT UNTIL (condition)</h4>
                <ul className="text-sm text-slate-700 mt-1 list-disc pl-5 space-y-1">
                  <li>Checks condition <em>before</em> each iteration.</li>
                  <li>If <strong>false</strong>, the code runs.</li>
                  <li>If <strong>true</strong>, the loop <strong>stops</strong>.</li>
                </ul>
                <p className="text-xs text-red-600 mt-2 font-bold">⚠️ Danger: Infinite Loops occur if the condition never becomes true.</p>
              </div>
              <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                <h4 className="font-bold text-sky-900">3. FOR EACH item IN list</h4>
                <p className="text-sm text-slate-700 mt-1">Traverses every element in a list one by one.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-9",
      title: "Topic 3.9: Developing Algorithms",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Algorithm Building Blocks</h3>
            <Mnemonic title="Algorithm Essentials" content="Every algorithm is built using: Sequencing (Order), Selection (If/Else), and Iteration (Loops)." />
            <p className="text-slate-700 mt-4 leading-relaxed text-sm">
              Different algorithms can solve the same problem but vary in <strong>efficiency, readability, and complexity</strong>.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-10",
      title: "Topic 3.10: Lists",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-red-800 border-b pb-2 mb-4">🚨 CRITICAL EXAM RULE</h3>
            <div className="bg-red-100 p-6 rounded-xl border-2 border-red-500 text-center">
              <h4 className="text-2xl font-black text-red-900 mb-2">1-BASED INDEXING</h4>
              <p className="text-red-800 font-medium">
                AP CSP Lists start at index <strong>1</strong>.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                <span className="bg-white px-3 py-1 rounded border border-red-200">First Item: <code className="font-bold">list[1]</code></span>
                <span className="bg-white px-3 py-1 rounded border border-red-200">Last Item: <code className="font-bold">list[LENGTH(list)]</code></span>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ List Operations</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <code className="text-xs font-bold text-orange-900">INSERT(list, i, value)</code>
                <p className="text-[11px] text-slate-600 mt-1">Shifts existing items to the right. Increases length.</p>
              </div>
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <code className="text-xs font-bold text-orange-900">APPEND(list, value)</code>
                <p className="text-[11px] text-slate-600 mt-1">Adds to the end. Increases length.</p>
              </div>
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <code className="text-xs font-bold text-orange-900">REMOVE(list, i)</code>
                <p className="text-[11px] text-slate-600 mt-1">Removes item; shifts remaining items left. Decreases length.</p>
              </div>
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <code className="text-xs font-bold text-orange-900">LENGTH(list)</code>
                <p className="text-[11px] text-slate-600 mt-1">Returns number of items currently in the list.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-11",
      title: "Topic 3.11: Binary Search",
      content: (
        <div className="space-y-6">
          <section>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Linear Search</h4>
                <ul className="text-xs text-slate-600 mt-2 space-y-1">
                  <li>• Checks items one-by-one.</li>
                  <li>• Requirement: <strong>None</strong> (works on unsorted).</li>
                  <li>• Efficiency: Checks <InlineMath math="N" /> items.</li>
                </ul>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Binary Search</h4>
                <ul className="text-xs text-slate-600 mt-2 space-y-1">
                  <li>• Splits list in half repeatedly.</li>
                  <li>• Requirement: <strong>MUST BE SORTED</strong>.</li>
                  <li>• Efficiency: Logarithmic (<InlineMath math="\log_2 N" />).</li>
                </ul>
              </div>
            </div>
            <ImportantNote>
              Binary Search is significantly faster for large data. Searching 1024 sorted numbers takes at most 10 steps (<InlineMath math="2^{10} = 1024" />).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-12",
      title: "Topic 3.12: Calling Procedures",
      content: (
        <div className="space-y-6">
          <section>
            <KeyTerm 
              term="Procedure" 
              definition="A named group of programming instructions (functions/methods) that may have parameters and return values."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
              <h4 className="font-bold text-slate-800">Flow of Control</h4>
              <p className="text-sm text-slate-700">When called, the program jumps to the definition, runs the code, and then returns to the line immediately following the call.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-13",
      title: "Topic 3.13: Developing Procedures",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 Procedural Abstraction</h3>
            <p className="text-sm text-slate-700 mb-4">Solving a subproblem by creating a procedure. Allows modularity and code reuse.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 text-sm">Parameters</h4>
                <p className="text-xs text-slate-700">Variables defined in the procedure header (placeholders).</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900 text-sm">Arguments</h4>
                <p className="text-xs text-slate-700">The actual values sent to the procedure during the call.</p>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
              <h4 className="font-bold text-red-900">The RETURN Rule</h4>
              <p className="text-sm text-slate-700">When <code className="font-mono">RETURN</code> is executed, the procedure <strong>stops immediately</strong>, even if more code exists below it.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-14",
      title: "Topic 3.14: Libraries",
      content: (
        <div className="space-y-6">
          <section>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm 
                term="Library" 
                definition="A collection of procedures useful for creating programs."
              />
              <KeyTerm 
                term="API" 
                definition="Application Programming Interface. Documentation explaining how library procedures behave and how to use them."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-15",
      title: "Topic 3.15: Random Values",
      content: (
        <div className="space-y-6">
          <section>
            <div className="bg-slate-800 text-white p-6 rounded-xl shadow-lg">
              <h4 className="font-mono text-xl font-bold mb-2">RANDOM(a, b)</h4>
              <p className="text-slate-300 text-sm">Generates a random integer between <code className="text-white">a</code> and <code className="text-white">b</code> <strong>INCLUSIVE</strong>.</p>
              <p className="text-xs text-slate-400 mt-4 italic">Example: RANDOM(1, 3) could result in 1, 2, or 3.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-16",
      title: "Topic 3.16: Simulations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Purpose of Simulation</h3>
            <p className="text-slate-700 leading-relaxed text-sm mb-4">
              An abstraction of a complex phenomenon to test hypotheses without real-world constraints.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
              {['Too Dangerous', 'Too Expensive', 'Too Slow', 'Too Fast'].map(t => (
                <div key={t} className="bg-blue-100 p-3 rounded-lg text-center text-xs font-bold text-blue-800 border border-blue-200">{t}</div>
              ))}
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mt-4">
              <h4 className="font-bold text-orange-900">Accuracy Constraint</h4>
              <p className="text-sm text-slate-700">Simulations simplify models; therefore, they may contain <strong>bias</strong> and are often less accurate than real experiments.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-17",
      title: "Topic 3.17: Algorithmic Efficiency",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Algorithm Classifications</h3>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">1. Reasonable Time (Polynomial)</h4>
                <p className="text-sm text-slate-700">Linear (<InlineMath math="n" />), Squared (<InlineMath math="n^2" />), etc. Efficiency scales reasonably as input grows.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">2. Unreasonable Time</h4>
                <p className="text-sm text-slate-700">Exponential (<InlineMath math="2^n" />) or Factorial (<InlineMath math="n!" />). Growth is so fast they cannot solve large problems.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧩 Heuristics</h3>
            <p className="text-sm text-slate-700">
              A technique that finds an <strong>approximate</strong> or "good enough" solution when finding the exact solution is unreasonable or impossible.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-18",
      title: "Topic 3.18: Undecidable Problems",
      content: (
        <div className="space-y-6">
          <section>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-800">Decidable</h4>
                <p className="text-xs text-slate-600 mt-1">Algorithm exists to answer "yes" or "no" for ALL inputs.</p>
              </div>
              <div className="bg-slate-800 p-4 rounded-lg border border-slate-900 shadow-sm text-white">
                <h4 className="font-bold text-slate-100">Undecidable</h4>
                <p className="text-xs text-slate-400 mt-1">No algorithm exists that can ALWAYS answer correctly for all inputs.</p>
              </div>
            </div>
            <div className="mt-4 p-4 border-l-4 border-slate-400 bg-slate-50 italic text-sm text-slate-700">
              <strong>The Halting Problem:</strong> A computer cannot examine code and determine purely logically if it will stop or run forever.
            </div>
          </section>
        </div>
      )
    },
    {
      id: "summary-table",
      title: "Reference Sheet Syntax Summary",
      content: (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left text-slate-700 border-collapse">
            <thead className="bg-slate-100 font-bold">
              <tr>
                <th className="p-2 border">Concept</th>
                <th className="p-2 border">Syntax</th>
                <th className="p-2 border">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="p-2 border font-medium">Assignment</td><td className="p-2 border font-mono">a ← exp</td><td className="p-2 border">Right to Left.</td></tr>
              <tr><td className="p-2 border font-medium">Index Access</td><td className="p-2 border font-mono">list[i]</td><td className="p-2 border font-bold text-red-600 underline">Starts at 1.</td></tr>
              <tr><td className="p-2 border font-medium">Iteration</td><td className="p-2 border font-mono">REPEAT UNTIL (c)</td><td className="p-2 border">Runs UNTIL condition true.</td></tr>
              <tr><td className="p-2 border font-medium">List Add</td><td className="p-2 border font-mono">INSERT(list, i, v)</td><td className="p-2 border">Shifts items right.</td></tr>
              <tr><td className="p-2 border font-medium">List Remove</td><td className="p-2 border font-mono">REMOVE(list, i)</td><td className="p-2 border">Shifts items left.</td></tr>
              <tr><td className="p-2 border font-medium">Return</td><td className="p-2 border font-mono">RETURN(exp)</td><td className="p-2 border">Ends procedure immediately.</td></tr>
            </tbody>
          </table>
        </div>
      )
    }
  ]
};

export default cspUnit3Notes;